/**
 * #2096 — the live chat data layer. Replaces #2091's invented data with the
 * real cs3-chat service.
 *
 * ══ THE SHAPE OF IT ══
 *
 *   CS3  POST /api/v2/chat/handoff        -> short-lived signed handoff
 *   CHAT POST /api/session/handoff        -> firebase token + 12h session token
 *                                            + this silo's Firebase web config
 *   Firebase signInWithCustomToken        -> the browser can now READ Firestore
 *   Firestore onSnapshot                  -> messages arrive
 *   CHAT POST /api/channels/{id}/messages -> messages leave
 *
 * ⚠ THE BROWSER NEVER WRITES TO FIRESTORE (spec §4). Posting goes through the
 * chat service's HTTP API so nothing can bypass sanitising, the permission
 * check, the RDS log or the billing count — and the Firestore rules deny
 * client writes outright, so a "shortcut" here would simply fail. Reads come
 * straight from Firestore because that is the live delivery channel.
 *
 * ══ TWO TOKENS, TWO ORIGINS, AND THEY MUST NOT CROSS ══
 *
 * The CS3 bearer belongs to CS3 and the chat session token belongs to the chat
 * service. `src/api.js`'s axios instance attaches the CS3 bearer plus the
 * cs3-context-* headers to EVERY request it makes — so calling the chat origin
 * through it would hand a third-party host a live CS3 session token. Every call
 * to the chat service in this file therefore goes through `chatHttp()`, a bare
 * axios instance that knows only the chat session token.
 *
 * ══ WHAT IS NOT HERE ══
 *
 * No settings writes (Configuration → Chat still edits the #2091 preview store
 * until its own issue), no membership sync (spec §8, a server-side job), no
 * FCM. Those are the chat service's #7-#18 and CS3's own follow-ups.
 */

import axios from 'axios';
import api from './api';
import { fieldErrorsFrom, settingsFailureCode, settingsClientId } from './chatSettings.js';
import { contactsFailureCode, normaliseContactGroups } from './chatContacts.js';
import {
    directFailureCode, normaliseDirect, normalisePeople, peopleFailureCode, searchable,
} from './chatPeople.js';
import { createPresence } from './chatPresence.js';
/*
 * #2108 — CallSteward Support: the availability answer and the ticket. The
 * rules (which reason offers a ticket, what each failure says) live in the
 * module so they can be tested; this file is only the two requests.
 */
import {
    isEmailRefusal, normaliseAvailability, ticketFailureCode, ticketReady,
} from './chatSupport.js';
import { forgetChatIdentity } from './chatSignOut.js';
/*
 * #2110 — the unread counts and the read mark (cs3-chat #29). The shapes and
 * the failure codes live in the module so they can be tested; this file is
 * only the two requests.
 */
import { isMessageId, normaliseUnread, unreadFailureCode } from './chatUnread.js';
import {
    ChatUnavailable, MAX_MESSAGE_CHARS, MAX_SCROLLBACK, PAGE_SIZE, ROSTER_PATH,
    applyChannelDoc, clampScrollback, colourFor, initialsFor,
    normaliseAttachments, normaliseChannel, normaliseMessage, rosterFailureCode,
} from './chatShapes.js';

/*
 * Re-exported so callers have one import for the whole data layer and do not
 * need to know which half a given name lives in.
 */
export {
    ChatUnavailable, MAX_MESSAGE_CHARS, MAX_SCROLLBACK, PAGE_SIZE, ROSTER_PATH,
    applyChannelDoc, clampScrollback, colourFor, initialsFor,
    normaliseAttachments, normaliseChannel, normaliseMessage, rosterFailureCode,
};

/*
 * One session per page. The handoff is single-use and the session token lasts
 * 12 hours, so re-handing-off on every call would be both wasteful and a
 * guaranteed replay rejection if two calls raced.
 */
let _session = null;
let _signingIn = null;

/*
 * Bumped by every reset. An in-flight sign-in compares the generation it
 * started in against this before publishing itself — see signIn().
 */
let _generation = 0;

/** Test seam, and what a sign-out would call. */
export function resetChatSession() {
    if (_session) {
        // Leave no listener attached to a session nobody owns any more — a
        // live Firestore listener keeps billing reads whether or not anything
        // is reading them (spec §12).
        (_session.unsubscribers || []).forEach((fn) => { try { fn(); } catch (e) { /* already gone */ } });
    }
    _session = null;
    _signingIn = null;

    /*
     * ⚠ #2103 — AND SIGN FIREBASE OUT. Dropping the session object left the
     * Firebase app authenticated: the panel stopped reading, and anyone with
     * devtools could carry on. Not awaited — a reset happens in
     * `beforeDestroy` and on a handoff rejection, neither of which may wait for
     * a network round trip — and it cannot throw (see chatSignOut).
     */
    forgetChatIdentity();
    /*
     * ⚠ INVALIDATES ANY SIGN-IN STILL IN FLIGHT.
     *
     * Without this, a reset during the handoff is ignored by the attempt that
     * is already running: it goes on to initialise Firebase, sign in, and
     * publish itself to `_session` — leaving an authenticated app and a cached
     * session that no component owns. The next mount then gets that session
     * straight from the cache, and if the user switched client in between it
     * carries the PREVIOUS cid, which is the value the service bills to.
     */
    _generation += 1;
}

export function currentSession() {
    return _session;
}

/**
 * Ask CS3 for a handoff.
 *
 * Goes through the app's own axios instance precisely BECAUSE it attaches the
 * CS3 bearer — this one is a CS3 endpoint. It is also the only call in this
 * file that uses it.
 */
export async function requestHandoff() {
    const res = await api.post('chat/handoff');
    const data = (res && res.data && res.data.data) || {};

    if (!data.token || !data.api_url) {
        throw new ChatUnavailable('cs3_handoff_incomplete');
    }

    return data;
}

/**
 * Redeem the handoff at the chat service.
 *
 * ⚠ NO CS3 CREDENTIAL ON THIS REQUEST. The handoff token IS the credential
 * (that is why it lives 90 seconds and is single-use), and the chat service's
 * handoff route is deliberately unauthenticated. Sending the CS3 bearer as
 * well would leak it to another host for no gain.
 */
export async function redeemHandoff(apiUrl, token) {
    try {
        const res = await axios.post(apiUrl + '/api/session/handoff', { token }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 15000,
        });

        return (res && res.data && res.data.data) || {};
    } catch (e) {
        /*
         * The chat service answers 401 `invalid_handoff_token` for EVERY
         * rejection — expired, tampered, wrong silo, already redeemed — on
         * purpose: telling an attacker which one it was confirms a token was
         * genuine. So there is nothing to inspect here, and the only correct
         * response to all of them is to ask CS3 for a new handoff.
         */
        throw new ChatUnavailable(status(e) === 401 ? 'handoff_rejected' : 'chat_unreachable', e);
    }
}

/**
 * Sign in: handoff -> redeem -> Firebase. Returns the session, or throws.
 *
 * Concurrent callers share one attempt. Two panels (docked and the #2091
 * pop-out) mount at once, and two handoffs racing means one of them is
 * redeemed second and refused as a replay.
 */
export function signIn() {
    if (_session) return Promise.resolve(_session);
    if (_signingIn) return _signingIn;

    const startedIn = _generation;

    _signingIn = (async () => {
        const handoff = await requestHandoff();
        const redeemed = await redeemHandoff(handoff.api_url, handoff.token);

        if (!redeemed.session_token) {
            throw new ChatUnavailable('chat_session_missing');
        }

        const session = {
            apiUrl: handoff.api_url,
            silo: handoff.silo,
            sessionToken: redeemed.session_token,
            expiresAt: Date.now() + (Number(redeemed.expires_in) || 0) * 1000,
            // The CA's master switch, already resolved server-side. With chat
            // off the panel must not render at all (spec §3 rule 1).
            chatVisible: redeemed.chat_visible !== false,
            uid: redeemed.uid,
            role: redeemed.role,
            cid: redeemed.cid,
            name: redeemed.name,
            // From CS3's handoff, not the chat service: CS3 owns client names.
            clientName: handoff.client_name || null,
            /*
             * #2117 — whose SETTINGS Configuration → Chat may open.
             *
             * From CS3's handoff (like `clientName`), not from the chat
             * service, and NOT the same thing as `cid`: CS staff are
             * client-less for messaging and billing (#2111) and still work
             * inside a client. CS3 resolved this from the working client and
             * checked it server-side; see `settingsClientId()` in
             * `chatSettings.js`.
             */
            settingsClientId: handoff.settings_client_id || null,
            /*
             * …and its name, for the screen's headings. Beside the id and from
             * the same client row server-side, so a heading cannot name a
             * different client from the one being saved. `clientName` above
             * remains the SESSION's client, which for CS staff is null — see
             * configChat.vue, which prefers this and falls back to that.
             */
            settingsClientName: handoff.settings_client_name || null,
            /*
             * #2108 — does CS3 have an email address for this person?
             *
             * ⚠ A BOOLEAN, NEVER THE ADDRESS. The ticket form needs to say "we
             * need an email address for you" BEFORE someone types a message
             * and has it refused — and the panel has no other use for the
             * address itself. CS3 puts the real value in the signed handoff
             * token, where only the chat service reads it; the panel is not a
             * directory (the same line `ChatHandoffController::displayName`
             * draws about names).
             */
            hasEmail: handoff.has_email === true,
            firebaseConfig: redeemed.firebase || null,
            firebaseToken: redeemed.firebase_token || null,
            firebaseApp: null,
            firestore: null,
            unsubscribers: [],
        };

        // Only sign in to Firebase when there is something to read. A client
        // whose chat is off gets a session (so the panel can say why) and no
        // listener.
        if (session.chatVisible && session.firebaseToken && session.firebaseConfig) {
            await attachFirebase(session);
        }

        // Someone reset us while this was in flight — most likely the panel
        // unmounting, possibly a client switch. Publishing now would install a
        // session nobody asked for; see resetChatSession().
        if (startedIn !== _generation) {
            throw new ChatUnavailable('chat_sign_in_abandoned');
        }

        _session = session;

        return session;
    })();

    const attempt = _signingIn;

    // Release the latch whichever way it went, so a failure can be retried
    // rather than cached forever. Guarded on identity: a reset during the
    // attempt must not have its fresh latch cleared by this one finishing.
    const release = () => { if (_signingIn === attempt) _signingIn = null; };
    attempt.then(release, release);

    return attempt;
}

/**
 * Initialise Firebase for THIS silo and sign in with the custom token.
 *
 * ⚠ The config comes from the handoff response, never from the bundle. A .ca
 * user must land on the Canadian project, and stage- must land on
 * `callsteward-chat-com-stage` and never on a production chat project
 * (spec decision #31) — the server decides which, and the browser is not told
 * in advance which one it is talking to.
 *
 * Lazy-loaded with the same `webpackChunkName: "firebase"` as
 * `utils/firebase-messaging.js`, so the Firestore SDK is not in the main
 * bundle for the users who never open chat.
 */
async function attachFirebase(session) {
    const { initializeApp, getApps } = await import(/* webpackChunkName: "firebase" */ 'firebase/app');
    const {
        getAuth, inMemoryPersistence, setPersistence, signInWithCustomToken,
    } = await import(/* webpackChunkName: "firebase" */ 'firebase/auth');
    const { getFirestore } = await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    /*
     * A NAMED app, not the default one. `utils/firebase-messaging.js` already
     * calls initializeApp() for FCM against a DIFFERENT project, and a second
     * initializeApp() with no name either throws or silently returns the first
     * app — which would have chat reading the wrong project's Firestore.
     */
    const NAME = 'cs3-chat';
    const existing = getApps().find((a) => a.name === NAME);
    session.firebaseApp = existing || initializeApp(session.firebaseConfig, NAME);

    const auth = getAuth(session.firebaseApp);

    /*
     * ⚠ #2103 — IN MEMORY, AND SET BEFORE THE SIGN-IN.
     *
     * Firebase's default web persistence writes the signed-in user to IndexedDB
     * (`firebase:authUser:<apiKey>:cs3-chat`), where it outlives both the tab
     * and the CS3 session. Nothing removed it on logout — so on a shared
     * dispatch desk the next person at that browser still held a valid Firebase
     * identity for the previous user and could read their Firestore chat
     * straight from devtools. The CS3 session was gone; the chat one was not.
     *
     * `inMemoryPersistence` ties the sign-in to the page: a reload or a close
     * ends it, and the handoff mints a new one. That costs one extra handoff per
     * page load, which is what the handoff is for — it is single-use and
     * short-lived by design.
     *
     * ⚠ AND IT MUST BE SET BEFORE `signInWithCustomToken`. Afterwards, the user
     * has already been written to disk and changing the persistence does not
     * recall it. Awaited for the same reason.
     *
     * `chatSignOut.clearPersistedChatAuth` handles the other half: the entries
     * EARLIER BUILDS already wrote, which in-memory persistence cannot remove.
     */
    await setPersistence(auth, inMemoryPersistence);
    await signInWithCustomToken(auth, session.firebaseToken);

    session.firestore = getFirestore(session.firebaseApp);
}

/** A bare axios call to the chat service, carrying only its own session token. */
function chatHttp(session, method, path, body) {
    return axios({
        method,
        url: session.apiUrl + path,
        data: body,
        timeout: 15000,
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + session.sessionToken,
        },
    });
}

/**
 * The channels this user can see.
 *
 * ⚠ The channel list comes from the SERVER, and the panel does not build
 * channel ids itself. The id format is the chat service's (spec §7:
 * `c<clientId>_<kind>`, `g_<key>`, `cs_admin`) and deriving it client-side
 * would be a second, drifting implementation of someone else's key.
 */
export async function fetchChannels(session) {
    /*
     * ⚠ `/api/me/channels` — THE ROSTER — AND NOT THE ADMIN LISTING.
     *
     * This used to call `/api/clients/{cid}/channels`, which is the
     * Configuration → Chat ADMIN listing, gated on cs3-chat's
     * `ChatPolicy::canManageClient`. So a Client Admin got their channels and
     * EVERY OTHER ROLE got 403 and no chat at all. Measured on stage
     * 2026-10-19: Ed (`ca`) 200; CallSteward Test (`member`) 403 on every
     * attempt, while that same member could read the channel documents and
     * their own member document straight from Firestore. The data and the
     * rules were right; only the list endpoint refused.
     *
     * cs3-chat #24 added this endpoint for the question the panel is actually
     * asking — "what may I read" rather than "what does this client have" —
     * computed for the session's own actor with the same predicate the
     * Firestore rules use.
     *
     * No client id in the path, so CS staff need no separate branch either:
     * #24 answers for them too (their silo), where the old code had to fall
     * back to `/api/channels` because they have no cid.
     */
    try {
        const res = await chatHttp(session, 'get', ROSTER_PATH);

        return ((res && res.data && res.data.data) || []).map(normaliseChannel);
    } catch (e) {
        /*
         * ⚠ The mapping lives in `chatShapes.js` so it can be tested — this
         * module does not load under node. It is worth testing specifically:
         * before #2096's review round a 403 fell through to the catch-all, and
         * "Chat is unreachable" for a permission refusal is what sent Ed
         * looking at the wrong layer.
         */
        throw new ChatUnavailable(rosterFailureCode(status(e)), e);
    }
}

/**
 * #2099 — read the client's chat settings (Configuration → Chat).
 *
 * The CA's switches live in the chat service, not in CS3: it is the service
 * that bills, that the Firestore rules read, and that the membership sync asks
 * before creating a channel. So this screen is a client of the same API the
 * panel uses, with the same session token.
 */
export async function fetchClientSettings(session) {
    /*
     * #2117 — the client whose settings these are, which for CS staff is the
     * one they are WORKING IN rather than one of their own. `cid` is still
     * null for them and nothing is billed; see `settingsClientId()`.
     *
     * No working client at all — staff on the admin home — still refuses, and
     * the screen still says to pick a local.
     */
    const clientId = settingsClientId(session);
    if (!clientId) {
        throw new ChatUnavailable('chat_no_client');
    }

    try {
        const res = await chatHttp(session, 'get',
            '/api/clients/' + encodeURIComponent(clientId) + '/settings');

        return (res && res.data && res.data.data) || null;
    } catch (e) {
        throw settingsFailure(e);
    }
}

/**
 * Save one or more SECTIONS of the settings.
 *
 * ⚠ SECTIONS, NOT KEYS. The API replaces each section it is sent whole and
 * leaves out what is absent, which is what lets the on/off switch save without
 * re-sending the schedule. Sending a partial section would blank the rest of
 * it — see `outsideHoursPatch`, which carries the server's `people` through for
 * exactly that reason.
 */
export async function saveClientSettings(session, patch) {
    const clientId = settingsClientId(session);
    if (!clientId) {
        throw new ChatUnavailable('chat_no_client');
    }

    try {
        const res = await chatHttp(session, 'put',
            '/api/clients/' + encodeURIComponent(clientId) + '/settings', patch);

        return (res && res.data && res.data.data) || null;
    } catch (e) {
        throw settingsFailure(e);
    }
}

/**
 * The settings API's failures, as codes the screen can explain.
 *
 * ⚠ CARRIES THE FIELD MESSAGES ON A 422. The API answers with exactly what the
 * screen needs — `errors: {"office_hours.days.mon.to": ["Closing time must be
 * after opening time."]}` — and it is two clicks away (set Monday 09:00 →
 * 08:00, or clear a time input). Collapsing it to a bare code leaves the Client
 * Admin watching their edit spring back with no field and no reason, which is
 * what #2099 asked not to happen.
 *
 * The mapping itself lives in `chatSettings.js` so it can be tested; this
 * module does not load under node.
 */
function settingsFailure(e) {
    const refused = new ChatUnavailable(settingsFailureCode(status(e)), e);
    refused.fieldErrors = fieldErrorsFrom(e && e.response && e.response.data);

    return refused;
}

/**
 * Listen to each channel's own Firestore DOCUMENT.
 *
 * ⚠ THIS, NOT A MESSAGE LISTENER, IS WHAT DRIVES THE CHANNEL LIST AND THE
 * TOASTS — spec §7: "Each panel listens to its channels' docs to drive
 * toasts". The distinction is the whole cost model:
 *
 *   channel doc        1 read to attach, 1 per new message. Spec §12's
 *                      "1 read per channel listener attached".
 *   messages, limit 50 about 50 reads to attach. Spec §12 prices that
 *                      separately, as "opening a channel".
 *
 * Attaching a 50-message listener to every channel a user belongs to would
 * therefore cost ~50x what the model budgets, on every mount of the panel —
 * and Firestore has no hard spend cap (spec §12). Messages are loaded for the
 * channel the user actually opens, and nowhere else.
 *
 * One listener per document rather than one query over the collection: a
 * collection query has to satisfy the Firestore rules for every document it
 * could return, and a per-document read is the thing the rules are written
 * against (`canReadChannel`).
 *
 * Returns one unsubscribe function covering all of them.
 */
export async function listenToChannelDocs(session, channelIds, onChannelDoc, onError) {
    if (!session.firestore) {
        throw new ChatUnavailable('chat_not_signed_in');
    }

    const { doc, onSnapshot } = await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    const stops = channelIds.map((id) => onSnapshot(
        doc(session.firestore, 'channels', id),
        (snap) => { if (snap.exists()) onChannelDoc(id, snap.data()); },
        (err) => { if (onError) onError(id, new ChatUnavailable('chat_listen_failed', err)); }
    ));

    const stopAll = () => stops.forEach((stop) => { try { stop(); } catch (e) { /* already gone */ } });
    session.unsubscribers.push(stopAll);

    return () => {
        const at = session.unsubscribers.indexOf(stopAll);
        if (at >= 0) session.unsubscribers.splice(at, 1);
        stopAll();
    };
}

/**
 * Listen to a channel's messages.
 *
 * Returns an unsubscribe function. Newest-first from Firestore (that is the
 * only direction an index can serve a "latest N" query), reversed for display,
 * because a chat reads oldest-at-the-top.
 *
 * ⚠ `limit` is capped at MAX_SCROLLBACK whatever the caller asks. The Firestore
 * rules REFUSE a larger query, and a refused listener is a silent empty panel
 * rather than an error — so the cap is applied here where it can be seen.
 */
export async function listenToChannel(session, channelId, limit, onMessages, onError) {
    if (!session.firestore) {
        throw new ChatUnavailable('chat_not_signed_in');
    }

    const { collection, onSnapshot, orderBy, query, limit: fsLimit } =
        await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    const take = clampScrollback(limit);

    const q = query(
        collection(session.firestore, 'channels', channelId, 'messages'),
        orderBy('createdAt', 'desc'),
        fsLimit(take)
    );

    const stop = onSnapshot(q,
        (snap) => {
            const rows = [];
            snap.forEach((doc) => rows.push(normaliseMessage(doc.id, doc.data())));
            rows.reverse();
            onMessages(rows);
        },
        (err) => {
            /*
             * A rules refusal and a dropped connection both arrive here. Worth
             * surfacing rather than swallowing: a listener that fails silently
             * is the "chat shows nothing and reports nothing" state, and
             * Firestore will keep retrying a failing listener — which is real
             * money (spec §12: there is no hard spend cap).
             */
            if (onError) onError(new ChatUnavailable('chat_listen_failed', err));
        }
    );

    session.unsubscribers.push(stop);

    return () => {
        const at = session.unsubscribers.indexOf(stop);
        if (at >= 0) session.unsubscribers.splice(at, 1);
        stop();
    };
}

/**
 * #2102 — upload one file to a channel, with progress and a cancel.
 *
 * cs3-chat #25's endpoint: multipart `file`, 201 with `{id, name, type, size}`.
 * ⚠ THE BYTES GO THROUGH THE CHAT SERVICE, NOT TO S3. The server sniffs the
 * content (a browser's `File.type` is the OS guessing from the extension) and
 * applies the posting rules before anything is stored, so there is no
 * pre-signed PUT to make here and no unchecked object in the bucket.
 *
 * ⚠ AND NOT THROUGH `src/api.js`. Its axios instance attaches the CS3 bearer
 * and the `cs3-context-*` headers to every request — sending a 10 MB body to
 * the chat origin through it would hand a third-party host a live CS3 session
 * token. `chatHttp` exists for that reason, and this builds on the same bare
 * instance.
 *
 * @param {File} file
 * @param {function(number):void} onProgress  0..100, called as bytes leave
 * @param {AbortSignal} signal                from the panel's cancel button
 */
export async function uploadAttachment(session, channelId, file, onProgress, signal) {
    const form = new FormData();
    /*
     * ⚠ THE FILENAME IS PASSED EXPLICITLY. Some browsers send `blob` as the
     * part's filename when a File is appended without one, and that name is
     * what the panel shows beside the attachment afterwards. The server
     * sanitises it (path and control characters) but cannot invent it.
     */
    /*
     * ⚠ #2103 — THE NAME IS THE THIRD ARGUMENT, and that is what makes a pasted
     * image work even where the `File` constructor is missing: the name travels
     * with the part rather than on the object. `pastedName` supplies it when the
     * rename worked; the clipboard's own name when it did not.
     */
    form.append('file', file, file.name);

    try {
        const res = await axios({
            method: 'post',
            url: session.apiUrl + '/api/channels/' + encodeURIComponent(channelId) + '/attachments',
            data: form,
            /*
             * ⚠ NO EXPLICIT Content-Type. The browser has to set
             * `multipart/form-data; boundary=…` itself — writing the header by
             * hand drops the boundary and the server sees a body it cannot
             * parse, which presents as "no file was sent".
             */
            headers: { Authorization: 'Bearer ' + session.sessionToken },
            // A 10 MB upload over a venue's wifi is not a 15-second request.
            timeout: 120000,
            signal,
            onUploadProgress: (e) => {
                if (typeof onProgress !== 'function') return;
                // `total` is absent on some proxies; report nothing rather than
                // NaN, and the panel shows an indeterminate bar.
                const total = Number(e && e.total);
                if (!isFinite(total) || total <= 0) return;
                onProgress(Math.min(100, Math.round((Number(e.loaded) || 0) / total * 100)));
            },
        });

        return (res && res.data && res.data.data) || null;
    } catch (e) {
        /*
         * A cancel is not a failure, and it must not be reported as one. axios
         * raises a CanceledError whose `code` is ERR_CANCELED; the panel removes
         * the row and says nothing.
         */
        if (axios.isCancel && axios.isCancel(e)) throw new ChatUnavailable('upload_cancelled', e);

        throw new ChatUnavailable(attachmentFailureCode(e), e);
    }
}

/**
 * The server's refusal, as a code the panel has a sentence for.
 *
 * ⚠ THE BODY'S `reason` IS PREFERRED OVER THE STATUS, because one status covers
 * several reasons: 422 is "too large" or "wrong type", and 403 is any of the
 * posting refusals (office hours, archived, a job outside its window, a
 * conversation whose other person has gone). The status alone could only ever
 * produce one sentence for all of them.
 */
function attachmentFailureCode(e) {
    const body = e && e.response && e.response.data;
    const reason = body && body.reason;
    if (typeof reason === 'string' && reason !== '') return reason;

    return {
        401: 'chat_session_expired',
        403: 'not_permitted',
        404: 'chat_channel_gone',
        413: 'attachment_too_large',
        422: 'attachment_wrong_type',
        503: 'attachment_store_failed',
    }[status(e)] || 'chat_unreachable';
}

/**
 * #2102 — fetch one attachment's bytes, through a short-lived pre-signed URL.
 *
 * Two requests, and both are necessary:
 *
 *   1. `GET /api/attachments/{id}` — the chat service re-asks
 *      `ChatPolicy::canRead` and signs a URL good for five minutes;
 *   2. a GET of that URL — S3 serves the object.
 *
 * ⚠ THE SECOND REQUEST IS CROSS-ORIGIN TO S3, so the bucket needs a CORS rule
 * allowing GET from the CS3 origins. Without one the fetch fails and there is
 * nothing in the response to say why — a CORS failure is deliberately opaque to
 * script. That is why this returns null rather than throwing on a network
 * error, and why the panel falls back to a download chip: a plain link is a
 * top-level navigation, which CORS does not touch, so the file still opens even
 * when the thumbnail cannot.
 *
 * ⚠ AND THE URL IS NOT RETURNED. It expires in five minutes and cannot be
 * renewed, so a caller holding one would have a thumbnail that works for five
 * minutes and 403s afterwards. The BYTES are what is cached (chatAttachments
 * `createFileCache`).
 *
 * @returns {Promise<{blob: Blob, name: string, type: string}|null>}
 */
export async function fetchAttachment(session, attachmentId) {
    let signed;
    try {
        const res = await chatHttp(session, 'get', '/api/attachments/' + encodeURIComponent(attachmentId));
        signed = (res && res.data && res.data.data) || null;
    } catch (e) {
        throw new ChatUnavailable(attachmentFailureCode(e), e);
    }

    if (!signed || !signed.url) {
        throw new ChatUnavailable('attachment_store_failed');
    }

    try {
        /*
         * ⚠ A BARE axios, WITH NO HEADERS AT ALL. The pre-signed URL carries
         * its own authorisation in the query string, and an `Authorization`
         * header alongside it makes S3 refuse the request outright — it will
         * not accept two credentials. This is also why `chatHttp` is not used
         * here: it would attach the chat session token to a request to Amazon.
         */
        const file = await axios.get(signed.url, { responseType: 'blob', timeout: 60000 });

        return { blob: file.data, name: signed.name, type: signed.type };
    } catch (e) {
        /*
         * A CORS rule missing on the bucket, a network drop, or an expired
         * link. Null rather than a throw: the panel's answer to all three is
         * the same — show the chip with a download link, which works without
         * CORS.
         */
        console.warn('[chat] could not fetch an attachment\'s bytes', e);

        return null;
    }
}

/**
 * #2102 — a URL for opening a PDF in a new tab, or downloading a file.
 *
 * ⚠ THE PRE-SIGNED URL ITSELF, not a blob. A top-level navigation is not
 * subject to CORS, so this works even on a bucket with no CORS rule — and
 * cs3-chat serves the object `Content-Disposition: attachment`, so the browser
 * downloads it rather than rendering it. That is deliberate for the one
 * accepted type that can carry script.
 *
 * Fetched at the moment of the click and never stored: five minutes is enough
 * to open a tab and far too short to keep.
 */
export async function attachmentUrl(session, attachmentId) {
    try {
        const res = await chatHttp(session, 'get', '/api/attachments/' + encodeURIComponent(attachmentId));
        const signed = (res && res.data && res.data.data) || null;

        return (signed && signed.url) || null;
    } catch (e) {
        throw new ChatUnavailable(attachmentFailureCode(e), e);
    }
}

/**
 * #2098 — people this user may find, by display-name prefix.
 *
 * cs3-chat's `DirectReach` applies Ed's rule server-side: anyone in the local
 * the searcher is logged into, plus CS staff — unless All Members is switched
 * off, in which case only people sharing a switched-on group the searcher can
 * see. ⚠ NONE OF THAT IS RE-IMPLEMENTED HERE, and it must not be: a second
 * reading of that rule in the browser could only ever disagree with the one
 * that decides, and the direction it disagreed in would be either a colleague
 * who cannot be found or a name shown to someone who may not message them.
 *
 * Short queries are not sent at all. The server returns `[]` below its own
 * floor rather than an error, so a request per keystroke would be a round trip
 * that can only answer nothing.
 *
 * @returns {Promise<Array>} normalised people, newest server order preserved
 */
export async function searchPeople(session, query) {
    if (!searchable(query)) return [];

    try {
        const res = await chatHttp(session, 'get',
            '/api/me/people?q=' + encodeURIComponent(String(query).trim()));

        return normalisePeople((res && res.data && res.data.data) || []);
    } catch (e) {
        throw new ChatUnavailable(peopleFailureCode(status(e)), e);
    }
}

/**
 * #2098 — open, or find, the one-to-one conversation with a person.
 *
 * Idempotent on the server: the channel id is derived from the sorted pair of
 * uids, so this returns the same channel however many times it is called and
 * from either side. `created` says which it was, for a first-open note.
 *
 * ⚠ THE RULE IS RE-CHECKED HERE BY THE SERVER, and a 403 is the normal answer
 * to a stale search result — someone who has left the client, or whose group
 * was switched off since the list was drawn. `directFailureCode` maps it to
 * `direct_not_allowed` so the panel can say that rather than "chat is
 * unavailable".
 */
/**
 * #2118 — the employer's contacts: each linked client's PUs and Stewards.
 *
 * ⚠ IT TAKES NOTHING FROM THE CALLER. The chat session bearer is the whole
 * request — cs3-chat reads the employer from it, applies the per-client access
 * rule (#2115's `employer_clients`) and omits clients with no PU or Steward. A
 * client id in the path or a role in the body would be the browser asking for a
 * list it is not entitled to decide.
 *
 * Every role but employer gets `[]`, which is NOT the same as "show nothing" —
 * see `showsContacts` in `chatContacts.js`, where the role decides whether the
 * section exists at all.
 */
export async function fetchContacts(session) {
    try {
        const res = await chatHttp(session, 'get', '/api/me/contacts');

        return normaliseContactGroups((res && res.data && res.data.data) || []);
    } catch (e) {
        throw new ChatUnavailable(contactsFailureCode(status(e)), e);
    }
}

export async function openDirect(session, uid) {
    try {
        const res = await chatHttp(session, 'post', '/api/me/direct', { uid });

        return normaliseDirect((res && res.data && res.data.data) || {});
    } catch (e) {
        throw new ChatUnavailable(directFailureCode(status(e)), e);
    }
}

/**
 * #2108 — is CallSteward Support live right now?
 *
 * cs3-chat #27: `{live, reason: open|outside_hours|no_agent_online|not_configured}`.
 * The reasons are FreeScout's hours and whether any CS Admin or CS Support has
 * a tab open; the panel shows live chat or the ticket form from the answer.
 *
 * ⚠ IT DOES NOT THROW. There is nothing useful for a caller to do with a
 * failure here: the support conversation works either way — it is an ordinary
 * channel and any agent reads it when they next look — so an unanswerable
 * question becomes `availabilityFailed()`'s "we could not check" rather than an
 * error that would leave the panel with no state to render. See that function
 * for why this is the one place in chat that fails OPEN.
 */
export async function fetchSupportAvailability(session) {
    try {
        const res = await chatHttp(session, 'get', '/api/support/availability');

        return normaliseAvailability((res && res.data && res.data.data) || {});
    } catch (e) {
        console.warn('[chat] could not read support availability', e);

        return null;
    }
}

/**
 * #2110 — the unread counts, from the server.
 *
 * cs3-chat #29: `GET /api/me/unread` → `{ total, channels: [{channel_id,
 * unread}] }`, counted from the read position over the channels the roster says
 * this person may read, and leaving out the person's own messages. Channels
 * with nothing unread are LEFT OUT of the list, which `applyUnreadCounts` turns
 * into zeros on the roster rows.
 *
 * ⚠ THIS IS THE ONLY SOURCE OF THE BADGE. The panel used to add one per
 * incoming message in memory — a count that reset on reload and differed per
 * tab. It does not count any more; it asks.
 *
 * @returns {Promise<{ total: number, counts: Object<string, number> }>}
 */
export async function fetchUnread(session) {
    try {
        const res = await chatHttp(session, 'get', '/api/me/unread');

        return normaliseUnread((res && res.data && res.data.data) || {});
    } catch (e) {
        throw new ChatUnavailable(unreadFailureCode(status(e)), e);
    }
}

/**
 * #2110 — tell the server where this person has read up to, and whether the
 * conversation is on their screen.
 *
 * cs3-chat #29: `PUT /api/channels/{id}/read { message_id?, viewing? }`. The
 * mark only moves forward on the server, so a stale or repeated mark is
 * harmless; `viewing: true` starts a 45-second "has it open" lease the push
 * honours, and `viewing: false` ends it at once. ⚠ `viewing` is ALWAYS sent
 * explicitly: the server defaults a missing value to true, and "I have stopped
 * looking" must never be lost to an omitted field.
 *
 * ⚠ THE MESSAGE ID MUST BE ONE OF THIS CHANNEL'S. Anything else is a 404 —
 * deliberately, because a mark above every real message could never be moved
 * back down. The panel passes `newestShownId` of what Firestore delivered for
 * the open channel and nothing else; an id that is not even ULID-shaped is
 * dropped here rather than sent as a 422.
 *
 * @returns {Promise<{ channel_id: string, last_read_message_id: ?string, viewing_until: ?string, unread: number }>}
 */
export async function markRead(session, channelId, messageId, viewing) {
    const body = { viewing: viewing !== false };
    if (isMessageId(messageId)) body.message_id = messageId;

    try {
        const res = await chatHttp(session, 'put',
            '/api/channels/' + encodeURIComponent(channelId) + '/read', body);

        return (res && res.data && res.data.data) || {};
    } catch (e) {
        throw new ChatUnavailable(unreadFailureCode(status(e)), e);
    }
}

/**
 * #2108 — open a support ticket.
 *
 * ⚠ THIS CREATES A REAL CONVERSATION IN THE LIVE HELP DESK when the chat box is
 * configured for it (help.callsteward.com), which is why nothing in CS3 calls it
 * except the person pressing the button, and why the #2105 end-to-end spec and
 * every unit test stay away from it. host-pm arranges any live check with Ed.
 *
 * ⚠ NO ADDRESS IS SENT. cs3-chat creates the conversation for the email on the
 * person's own `chat_users` row — the one CS3's handoff put there — because an
 * address in this body would let any signed-in user open a ticket in somebody
 * else's name and have the replies emailed to them. A person with no address on
 * file is a 422 naming `email`, which `isEmailRefusal` turns into the sentence
 * the form shows.
 *
 * @throws ChatUnavailable  with `code` from `ticketFailureCode`, or
 *                          `ticket_no_email` for the 422 that names the address
 */
export async function submitSupportTicket(session, body) {
    const text = String(body == null ? '' : body).trim();
    if (!ticketReady(text)) throw new ChatUnavailable('ticket_rejected');

    try {
        const res = await chatHttp(session, 'post', '/api/support/ticket', { body: text });
        const data = (res && res.data && res.data.data) || {};

        if (!data.ticket) {
            // A 201 with no number is not a ticket the person can quote.
            throw new ChatUnavailable('support_unreachable');
        }

        return { ticket: data.ticket };
    } catch (e) {
        if (e instanceof ChatUnavailable) throw e;

        const code = status(e);
        if (code === 422 && isEmailRefusal(e && e.response && e.response.data)) {
            throw new ChatUnavailable('ticket_no_email', e);
        }

        throw new ChatUnavailable(ticketFailureCode(code), e);
    }
}

/**
 * #2098 / cs3-chat #23 — attach presence, if this silo has it.
 *
 * ⚠ RETURNS NULL RATHER THAN THROWING WHEN THERE IS NO `databaseURL`. Realtime
 * Database is enabled per Firebase project and the URL rides on the handoff's
 * web config; a silo where host-pm has not enabled it simply has no presence,
 * and chat must work exactly as before. The web SDK cannot reach RTDB without
 * that URL and would throw on `getDatabase` — which would take the whole
 * sign-in down for a feature that is only dots.
 *
 * Lazy-loaded under the same `webpackChunkName: "firebase"` as the rest, so
 * nothing is added to the main bundle.
 */
export async function attachPresence(session, handlers) {
    const config = session && session.firebaseConfig;
    if (!config || !config.databaseURL || !session.firebaseApp) return null;

    try {
        const {
            getDatabase, onDisconnect, onValue, ref, serverTimestamp, set,
        } = await import(/* webpackChunkName: "firebase" */ 'firebase/database');

        const presence = createPresence({
            db: getDatabase(session.firebaseApp),
            fns: { ref, onValue, set, onDisconnect, serverTimestamp },
            uid: session.uid,
            cid: session.cid,
            onChange: (handlers || {}).onChange,
            onOwn: (handlers || {}).onOwn,
        });
        presence.start();

        return presence;
    } catch (e) {
        // A project without RTDB, a blocked websocket, a corporate proxy. Dots
        // are a nicety; chat is not.
        console.warn('[chat] presence unavailable', e);

        return null;
    }
}

/**
 * Post a message.
 *
 * The 4,000-character limit is the chat service's (spec §5) and it enforces it;
 * checking here as well means the composer can refuse before spending a round
 * trip, and the panel already counts down to the same number.
 */
export async function sendMessage(session, channelId, body, attachmentIds = []) {
    const text = String(body == null ? '' : body).trim();
    const attachments = Array.isArray(attachmentIds)
        ? attachmentIds.filter((id) => typeof id === 'string' && id !== '')
        : [];

    /*
     * ⚠ #2102 — AN ATTACHMENT MAKES A WORDLESS MESSAGE A REAL MESSAGE. Sending
     * a photo with no caption is the normal case ("here's the gate"), and the
     * server's own validator was changed for it (`present|nullable` rather than
     * `required`). An empty message with nothing attached is still refused.
     */
    if (text === '' && attachments.length === 0) throw new ChatUnavailable('empty_message');
    if (text.length > MAX_MESSAGE_CHARS) throw new ChatUnavailable('message_too_long');

    try {
        const res = await chatHttp(session, 'post',
            '/api/channels/' + encodeURIComponent(channelId) + '/messages',
            // `attachments` omitted entirely when there are none, so a message
            // with no files is the exact request body it was before #2102.
            attachments.length ? { body: text, attachments } : { body: text });

        return (res && res.data && res.data.data) || null;
    } catch (e) {
        const code = status(e);

        /*
         * 403 is the interesting one and it is not an error in the panel's
         * sense: outside office hours, outside a job's 24h window, or an
         * archived channel all land here, and the panel should say which rather
         * than "sending failed". The server owns that decision — the panel's
         * own read-only state is a courtesy, not the gate.
         */
        if (code === 403) throw new ChatUnavailable('posting_closed', e);
        if (code === 401) throw new ChatUnavailable('chat_session_expired', e);
        if (code === 422) throw new ChatUnavailable('message_rejected', e);
        if (code === 429) throw new ChatUnavailable('too_many_messages', e);

        throw new ChatUnavailable('chat_unreachable', e);
    }
}

/** The HTTP status of an axios failure, or 0 when the request never landed. */
function status(e) {
    return (e && e.response && e.response.status) || 0;
}
